import { z } from 'zod';

export const stockStatusSchema = z.enum(['in_stock', 'out_of_stock', 'low_stock']);
export type StockStatus = z.infer<typeof stockStatusSchema>;

export const productVariantSchema = z.object({
  id: z.string(),
  nameAr: z.string(),
  nameEn: z.string(),
  colorNameAr: z.string(),
  colorNameEn: z.string(),
  colorHex: z.string(),
  sku: z.string(),
  priceModifier: z.number().optional(),
  stockStatus: stockStatusSchema,
});
export type ProductVariant = z.infer<typeof productVariantSchema>;

export const specIconSchema = z.enum(['monitor', 'storage', 'camera', 'color', 'battery', 'chip']);
export type SpecIcon = z.infer<typeof specIconSchema>;

export const productSpecSchema = z.object({
  icon: specIconSchema,
  label: z.string(),
  value: z.string(),
});
export type ProductSpec = z.infer<typeof productSpecSchema>;

export const featureIconSchema = z.enum(['cpu', '5g', 'water', 'battery', 'camera']);
export type FeatureIcon = z.infer<typeof featureIconSchema>;

export const productFeatureSchema = z.object({
  icon: featureIconSchema,
  label: z.string(),
  sublabel: z.string(),
});
export type ProductFeature = z.infer<typeof productFeatureSchema>;

export const badgeTypeSchema = z.enum(['samsung', 'apple', 'xiaomi', 'huawei', 'default']);
export type BadgeType = z.infer<typeof badgeTypeSchema>;

export const attributeSchema = z.object({
  key: z.string(),
  value: z.string(),
  icon: z.string().optional(),
});
export type ProductAttribute = z.infer<typeof attributeSchema>;

export const colorOptionSchema = z.object({
  nameAr: z.string(),
  nameEn: z.string().optional(),
  hex: z.string(),
});
export type ColorOption = z.infer<typeof colorOptionSchema>;

export const productSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  nameAr: z.string(),
  brandName: z.string(),
  brandSlug: z.string(),
  categorySlug: z.string(),
  price: z.number().nonnegative(),
  showPrice: z.boolean(),
  descriptionAr: z.string(),
  descriptionEn: z.string().optional(),
  imageUrl: z.string(),
  badgeType: badgeTypeSchema.optional(),
  specs: z.array(productSpecSchema),
  features: z.array(productFeatureSchema),
  variants: z.array(productVariantSchema),
  isFeatured: z.boolean(),
  isBestseller: z.boolean().optional(),
  attributes: z.array(attributeSchema).optional(),
  colors: z.array(colorOptionSchema).optional(),
  sku: z.string().optional(),
  stock: z.number().optional(),
  wholesalePrice: z.number().nonnegative().optional(),
});
export type Product = z.infer<typeof productSchema>;

export const brandLogoTypeSchema = z.enum([
  'samsung',
  'apple',
  'xiaomi',
  'redmi',
  'huawei',
  'oppo',
  'infinix',
  'tecno',
  'honor',
  'vivo',
  'poco',
  'oneplus',
  'google',
  'itel',
  'default',
]);
export type BrandLogoType = z.infer<typeof brandLogoTypeSchema>;

export const brandSchema = z.object({
  id: z.string().optional(),
  slug: z.string(),
  name: z.string(),
  nameAr: z.string(),
  logoType: brandLogoTypeSchema,
  accentColor: z.string(),
  logoUrl: z.string().optional(),
  categoryIds: z.array(z.string()).optional(),
});
export type Brand = z.infer<typeof brandSchema>;

export const deviceSchema = z.object({
  id: z.string(),
  brandId: z.string().nullable(),
  nameAr: z.string(),
  nameEn: z.string().nullable(),
  slug: z.string(),
  imageUrl: z.string().nullable().optional(),
  isActive: z.boolean(),
});
export type Device = z.infer<typeof deviceSchema>;
